import { useEffect, useRef, useState } from "react";
import { Share2, Download, X, Loader2, Instagram, Music, MessageCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = [
  { id: "instagram", label: "Instagram Story", w: 1080, h: 1920, icon: Instagram },
  { id: "tiktok", label: "TikTok", w: 1080, h: 1920, icon: Music },
  { id: "twitter", label: "X / Twitter", w: 1200, h: 675, icon: MessageCircle },
  { id: "square", label: "Square (Reddit, Threads)", w: 1080, h: 1080, icon: Share2 },
];

// Renders a Newseze share-card on a Canvas. Client-side, no server lambda.
function drawCard(ctx, data, w, h) {
  // Background — gradient navy
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, "#0a1f3d");
  g.addColorStop(1, "#1e40af");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  // Brand bar
  ctx.fillStyle = "#facc15";
  ctx.font = `900 ${Math.round(w * 0.025)}px -apple-system, Segoe UI, sans-serif`;
  ctx.fillText("NEWSEZE · BALANCED · TRUSTED · NEWS", w * 0.05, h * 0.08);

  // Trust badge — top right
  if (data.trust) {
    const bx = w * 0.78, by = h * 0.06;
    ctx.fillStyle = "#facc15";
    ctx.beginPath();
    ctx.roundRect(bx, by, w * 0.17, h * 0.045, 999);
    ctx.fill();
    ctx.fillStyle = "#1d1303";
    ctx.font = `900 ${Math.round(w * 0.022)}px -apple-system, Segoe UI, sans-serif`;
    const txt = `TRUST ${data.trust}`;
    const m = ctx.measureText(txt);
    ctx.fillText(txt, bx + (w * 0.17 - m.width) / 2, by + h * 0.032);
  }

  // Title — wrap text
  ctx.fillStyle = "#ffffff";
  const titleSize = w < 1100 ? Math.round(w * 0.052) : Math.round(w * 0.055);
  ctx.font = `900 ${titleSize}px -apple-system, Segoe UI, sans-serif`;
  const maxWidth = w * 0.9;
  const words = (data.title || "").split(" ");
  let line = "";
  let y = h * 0.32;
  for (const word of words) {
    const t = line + word + " ";
    if (ctx.measureText(t).width > maxWidth && line) {
      ctx.fillText(line, w * 0.05, y);
      y += titleSize * 1.18;
      line = word + " ";
    } else line = t;
  }
  if (line) ctx.fillText(line, w * 0.05, y);

  // Summary (only for tall formats)
  if (h / w > 1.2) {
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.font = `${Math.round(w * 0.028)}px -apple-system, Segoe UI, sans-serif`;
    const summaryWords = (data.summary || "").slice(0, 180).split(" ");
    let sline = "";
    let sy = h * 0.65;
    for (const word of summaryWords) {
      const t = sline + word + " ";
      if (ctx.measureText(t).width > maxWidth && sline) {
        ctx.fillText(sline, w * 0.05, sy);
        sy += w * 0.038;
        sline = word + " ";
      } else sline = t;
    }
    if (sline) ctx.fillText(sline, w * 0.05, sy);
  }

  // Source + CTA at bottom
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.font = `800 ${Math.round(w * 0.024)}px -apple-system, Segoe UI, sans-serif`;
  ctx.fillText(`Reporting: ${data.source || ""}`, w * 0.05, h * 0.88);

  ctx.fillStyle = "#facc15";
  ctx.font = `900 ${Math.round(w * 0.028)}px -apple-system, Segoe UI, sans-serif`;
  ctx.fillText("READ + EARN CREDITS · NEWSEZE.COM", w * 0.05, h * 0.94);
}

export default function ShareCardModal({ articleId, onClose }) {
  const [format, setFormat] = useState("instagram");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const canvasRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    axios.get(`${API}/students/share-card/${articleId}?format=${format}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [articleId, format]);

  useEffect(() => {
    if (!data || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = data.width;
    canvas.height = data.height;
    const ctx = canvas.getContext("2d");
    drawCard(ctx, data, data.width, data.height);
  }, [data]);

  const download = () => {
    if (!canvasRef.current) return;
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `newseze-${articleId}-${format}.png`;
    a.click();
  };

  const f = FORMATS.find((x) => x.id === format) || FORMATS[0];

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-[760px] w-full p-6 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="share-card-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[22px] font-black tracking-[-0.02em]">Share to socials</h3>
          <button onClick={onClose} className="text-[var(--muted)]" data-testid="share-card-close"><X size={20} /></button>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="share-card-formats">
          {FORMATS.map((opt) => {
            const Icon = opt.icon;
            return (
              <button key={opt.id} onClick={() => setFormat(opt.id)}
                className={`chip ${format === opt.id ? "!bg-[var(--blue)] !text-white !border-[var(--blue)]" : ""}`}
                data-testid={`share-format-${opt.id}`}>
                <Icon size={11} /> {opt.label}
              </button>
            );
          })}
        </div>

        <div className="bg-[var(--bg)] rounded-xl p-4 flex justify-center mb-4 max-h-[480px] overflow-auto">
          {busy ? <Loader2 className="animate-spin" /> : (
            <canvas ref={canvasRef} style={{ maxWidth: "100%", maxHeight: "440px", height: "auto" }} data-testid="share-card-canvas" />
          )}
        </div>

        {data && (
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <button onClick={download} className="btn primary" data-testid="share-card-download">
              <Download size={13} /> Download PNG
            </button>
            <a href={data.share_links.x} target="_blank" rel="noreferrer" className="btn" data-testid="share-card-x">X</a>
            <a href={data.share_links.tiktok} target="_blank" rel="noreferrer" className="btn" data-testid="share-card-tiktok">TikTok</a>
            <a href={data.share_links.instagram_story} target="_blank" rel="noreferrer" className="btn" data-testid="share-card-instagram">Instagram</a>
            <a href={data.share_links.threads} target="_blank" rel="noreferrer" className="btn" data-testid="share-card-threads">Threads</a>
            <a href={data.share_links.reddit} target="_blank" rel="noreferrer" className="btn" data-testid="share-card-reddit">Reddit</a>
          </div>
        )}
        <p className="text-[11.5px] text-[var(--muted)] italic">
          Download the PNG, then upload to your TikTok / Instagram story. The button next to it
          opens the platform's compose screen with your caption pre-filled.
        </p>
      </div>
    </div>
  );
}
